import React, { useEffect } from 'react';
import useBreakpoints from '@hooks/useBreakpoints';
import { injectTestId } from '@cvent/nucleus-test-automation';
import { APIErrorResponse, Channel, ErrorFromResponse } from 'stream-chat';
import { CSSObject } from '@emotion/react';
import { StreamType } from '@components/chat/shared/useStreamChat';

const useStyle = ({ isMobile }): Record<string, CSSObject> => ({
  container: {
    ...(isMobile
      ? {
          paddingBottom: '200px' // so the last item on the list does not hide behind the mobile browser navigation
        }
      : {
          height: '100%',
          overflowY: 'auto'
        })
  },
  channelList: {
    overflowY: 'auto',
    padding: '4px'
  }
});

interface MessagingChannelListProps {
  setIsLoadingChannels: (loading: boolean) => void;
  setLoadedChannels: (loadedChannels?: Channel<StreamType>[]) => void;
  setLoadingChannelsError: (error: ErrorFromResponse<APIErrorResponse> | null | boolean) => void;
  hasStartedLoadingChannels?: boolean;
  setHasStartedLoadingChannels?: (hasStartedLoadingChannels: boolean) => void;
  setIsCreatingChannel?: (isCreatingChannel: boolean) => void;
  // Below are props passed in from Stream
  loading?: boolean;
  error: ErrorFromResponse<APIErrorResponse> | null | boolean;
  children?: JSX.Element;
  loadedChannels?: Channel<StreamType>[];
}

function MessagingChannelList({
  setIsLoadingChannels,
  setLoadedChannels,
  setLoadingChannelsError,
  hasStartedLoadingChannels,
  setHasStartedLoadingChannels,
  // Below are props passed in from Stream
  loading,
  error = false,
  children,
  loadedChannels
}: MessagingChannelListProps): JSX.Element {
  const { isMobile } = useBreakpoints();
  const styles = useStyle({ isMobile });

  useEffect(() => {
    setLoadedChannels(loadedChannels);
  }, [loadedChannels, setLoadedChannels]);

  useEffect(() => {
    setIsLoadingChannels(loading);
    if (setHasStartedLoadingChannels && !hasStartedLoadingChannels && loading) {
      setHasStartedLoadingChannels(true);
    }
  }, [loading, setIsLoadingChannels, hasStartedLoadingChannels, setHasStartedLoadingChannels]);

  useEffect(() => {
    setLoadingChannelsError(error);
  }, [error, setLoadingChannelsError]);

  return (
    <div css={styles.container}>
      <div css={styles.channelList} {...injectTestId('attendee-messaging-existing-channels')}>
        {children}
      </div>
    </div>
  );
}

export default React.memo(MessagingChannelList);
